"use client";

import React from "react";
import { Shield, Zap, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const benefits = [{
  id: "create",
  title: "Create or Find an Order",
  description: "Start a food request or browse local listings from restaurants near you to hit order minimums without extra fees.",
  label: "STEP-01",
  icon: Shield
}, {
  id: "match",
  title: "Match & Coordinate",
  description: "Our real-time algorithm connects you with hungry neighbors instantly. Chat securely to finalize your shared meal.",
  label: "STEP-02",
  icon: Zap
}, {
  id: "split",
  title: "Split & Enjoy",
  description: "Save 50% on your favorite Toronto eats. Split the bill easily and enjoy your premium meal at half the cost.",
  label: "STEP-03",
  icon: CircleCheck
}];
interface AnimatedCardProps {
  benefit: typeof benefits[0];
  index: number;
  "data-index"?: number;
}
function AnimatedCard({
  benefit,
  index,
  "data-index": dataIndex
}: AnimatedCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    once: true
  });
  return <div data-section-id="181189" ref={ref} data-index={dataIndex} className={cn("transition-all duration-1000 ease-out h-full", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full bg-card/60 backdrop-blur-2xl border-border/40 shadow-xl hover:shadow-primary/10 transition-all duration-500 hover:-translate-y-2 group flex flex-col">
        <CardHeader className="pb-4 grow-0">
          <div className="flex justify-between items-start mb-8">
            <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors duration-500">
              <benefit.icon className="w-8 h-8 stroke-[1.5]" />
            </div>
            <Badge variant="outline" className="font-mono text-[10px] tracking-widest uppercase text-primary/80 bg-primary/5 border-primary/20">
              {benefit.label}
            </Badge>
          </div>
          <CardTitle className="font-sans text-2xl font-bold tracking-tight text-foreground">
            {benefit.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="grow">
          <CardDescription className="text-muted-foreground leading-relaxed text-base">
            {benefit.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function KeyBenefits() {
  return <section id="howhalforderworks" className="bg-background py-40 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {benefits.map((benefit, i) => <AnimatedCard key={benefit.id} benefit={benefit} index={i} data-index={i} />)}
        </div>
      </div>
    </section>;
}